import type { QuoteAnchor } from "./types";

const REVIEW_BLOCK_SELECTOR = "[data-review-block]";

export type SelectionPhase = "idle" | "stabilizing" | "ready" | "composing";

export interface SelectionCandidate {
  anchor: QuoteAnchor;
  range: Range;
  rect: DOMRect;
  signature: string;
}

export interface SelectionDraft {
  phase: SelectionPhase;
  anchor: QuoteAnchor | null;
  range: Range | null;
  rect: DOMRect | null;
  signature: string;
}

export type SelectionInspection =
  | { kind: "idle" }
  | { kind: "outside" }
  | { kind: "invalid"; message: string; signature: string }
  | { kind: "valid"; candidate: SelectionCandidate };

export function createEmptySelectionDraft(): SelectionDraft {
  return {
    phase: "idle",
    anchor: null,
    range: null,
    rect: null,
    signature: "",
  };
}

export function stabilizeSelectionDraft(
  current: SelectionDraft,
  candidate: SelectionCandidate,
): SelectionDraft {
  const stable = (current.phase === "stabilizing" || current.phase === "ready")
    && current.signature === candidate.signature;
  return {
    phase: stable ? "ready" : "stabilizing",
    anchor: candidate.anchor,
    range: candidate.range,
    rect: candidate.rect,
    signature: candidate.signature,
  };
}

export function beginSelectionComposition(current: SelectionDraft): SelectionDraft {
  if (!current.anchor) return current;
  return { ...current, phase: "composing" };
}

function closestReviewBlock(node: Node | null): HTMLElement | null {
  if (!node) return null;
  const element = node.nodeType === Node.ELEMENT_NODE
    ? (node as Element)
    : node.parentElement;
  return element?.closest<HTMLElement>(REVIEW_BLOCK_SELECTOR) || null;
}

function usableRects(range: Range): DOMRect[] {
  return Array.from(range.getClientRects()).filter((rect) => rect.width > 0 || rect.height > 0);
}

function selectionFocusRect(selection: Selection, range: Range): DOMRect | null {
  const rects = usableRects(range);
  if (!rects.length) {
    const fallback = range.getBoundingClientRect();
    return fallback.width > 0 || fallback.height > 0 ? DOMRect.fromRect(fallback) : null;
  }

  const focusIsStart = selection.focusNode === range.startContainer
    && selection.focusOffset === range.startOffset;
  return DOMRect.fromRect(focusIsStart ? rects[0] : rects[rects.length - 1]);
}

export function inspectReaderSelection(
  reader: HTMLElement,
  selection: Selection | null = window.getSelection(),
): SelectionInspection {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return { kind: "idle" };

  const range = selection.getRangeAt(0);
  const startBlock = closestReviewBlock(range.startContainer);
  const endBlock = closestReviewBlock(range.endContainer);
  if (!startBlock || !endBlock || !reader.contains(startBlock) || !reader.contains(endBlock)) {
    return { kind: "outside" };
  }

  const rawText = range.toString().trim();
  const signatureBase = `${startBlock.dataset.reviewBlock || "?"}:${range.startOffset}`
    + `-${endBlock.dataset.reviewBlock || "?"}:${range.endOffset}:${rawText}`;

  if (!rawText) return { kind: "idle" };
  if (rawText.length > 500) {
    return { kind: "invalid", message: "单次批注最多选择 500 个字", signature: signatureBase };
  }

  const before = document.createRange();
  before.selectNodeContents(reader);
  before.setEnd(range.startContainer, range.startOffset);
  const after = document.createRange();
  after.selectNodeContents(reader);
  after.setStart(range.endContainer, range.endOffset);
  const rect = selectionFocusRect(selection, range);
  if (!rect) {
    return { kind: "invalid", message: "无法定位当前选区，请重新选择", signature: signatureBase };
  }

  const anchor = {
    text: rawText,
    prefix: before.toString().slice(-120),
    suffix: after.toString().slice(0, 120),
  };
  const signature = `${signatureBase}:${anchor.prefix.length}:${anchor.suffix.length}`;
  return {
    kind: "valid",
    candidate: {
      anchor,
      range: range.cloneRange(),
      rect,
      signature,
    },
  };
}
